// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { AddOutlined, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { EligibleInstrument } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Types/module';
import { getSelectedAssets } from '../../utils/getSelectedAssets';

interface EligibleCollateralSelectProps {
  eligibleInstruments: EligibleInstrument[];
  onRemove: () => void;
  onChange: (field: string, value: string) => void;
  showClose?: boolean;
  selectedAssets?: { [key: string]: { [key: string]: boolean } };
}
const EligibleCollateralSelect: React.FC<EligibleCollateralSelectProps> = ({
  eligibleInstruments,
  onRemove,
  onChange,
  showClose,
  selectedAssets,
}) => {
  const [selected, setSelected] = React.useState('');
  const handleChange = (e: SelectChangeEvent) => {
    setSelected(e.target.value);
    const inst = eligibleInstruments.find((i) => i.instrument.id === e.target.value);
    if (!inst) {
      return;
    }
    const admin = inst?.instrument.admin;
    const id = inst?.instrument.id;
    onChange('id', id);
    onChange('admin', admin);
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mb: 1 }}>
        <Select
          fullWidth
          sx={{ mr: 1 }}
          displayEmpty
          placeholder="Select collateral"
          size="small"
          onChange={handleChange}
          value={selected}
        >
          {eligibleInstruments.map((i) => (
            <MenuItem
              disabled={selectedAssets?.[i.instrument.admin]?.[i.instrument.id]}
              value={i.instrument.id}
              key={i.instrument.id}
            >
              {i.instrument.id}
            </MenuItem>
          ))}
          <MenuItem value="" disabled>
            Select collateral
          </MenuItem>
        </Select>
        <TextField
          fullWidth
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onChange('amount', e.target.value);
          }}
          size="small"
          placeholder="amount"
        />
        {showClose && (
          <Box sx={{ ml: 0.5 }}>
            <IconButton size="small" onClick={onRemove}>
              <Close fontSize="small" color="action" />
            </IconButton>
          </Box>
        )}
      </Box>
    </Box>
  );
};

interface AssetSelectProps {
  eligibleInstruments: EligibleInstrument[];
  rows: { id: string; admin: string; amount: string; uuid: string }[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  updateEntry: (index: number, field: string, value: string) => void;
}
export const AssetSelect: React.FC<AssetSelectProps> = ({
  onRemove,
  onAdd,
  rows,
  eligibleInstruments,
  updateEntry,
}) => {
  const onChange = (i: number) => {
    return (field: string, value: string) => updateEntry(i, field, value);
  };
  const selected = getSelectedAssets(rows);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map((a, i) => (
        <EligibleCollateralSelect
          onRemove={() => onRemove(i)}
          eligibleInstruments={eligibleInstruments}
          key={a.uuid}
          selectedAssets={selected}
          onChange={onChange(i)}
          showClose={rows.length > 1}
        />
      ))}
      <Button
        sx={{ ml: 'auto' }}
        startIcon={<AddOutlined />}
        size="small"
        variant="text"
        onClick={onAdd}
      >
        Add Collateral
      </Button>
    </Box>
  );
};
